import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteAlarmPolicy, fetchAlarmTypes, upsertAlarmPolicy } from '../../api/ops';
import { Card, ProtocolTag } from '../../ui/components';

type Severity = api.AlarmSeverity;

/**
 * Settings → Alarms (user request 2026-10-08): every alarm type the system can raise, normalized across protocols, with
 * its condition, enabled/masked, severity and scope. presence ≠ fault ≠ alarm: masking a type never changes a device's
 * state, and its faults stay visible on the device page. Global changes need global alarm.configure (superadmin); since
 * 2026-10-10 the client users (site administrator, operator) do not see this tab.
 */
export function AlarmTypesSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scope, setScope] = useState('');
  const [error, setError] = useState<string | null>(null);
  const types = useQuery({ queryKey: ['alarm-types', scope], queryFn: () => fetchAlarmTypes(scope || null) });
  const sites = types.data?.manageable_sites ?? [];
  const canEdit = scope ? sites.some((s) => s.id === scope) : (types.data?.can_manage_global ?? false);
  const save = useMutation({
    mutationFn: upsertAlarmPolicy,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alarm-types'] }),
    onError: (e: Error) => setError(e.message),
  });
  const reset = useMutation({
    mutationFn: deleteAlarmPolicy,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alarm-types'] }),
    onError: (e: Error) => setError(e.message),
  });
  /** Current row of the edited scope (global row, or this client's override). */
  const own = (x: api.AlarmTypeItem) =>
    (scope ? x.overrides.find((o) => o.site_id === scope) : x.global) ?? null;
  const put = (
    x: api.AlarmTypeItem,
    patch: Partial<Pick<api.AlarmPolicyUpsert, 'enabled' | 'severity' | 'params'>>,
  ) => {
    const cur = own(x);
    setError(null);
    save.mutate({
      alarm_type: x.key,
      site_id: scope || null,
      enabled: patch.enabled !== undefined ? patch.enabled : (cur?.enabled ?? null),
      severity: patch.severity !== undefined ? patch.severity : (cur?.severity ?? null),
      params: (patch.params ?? cur?.params ?? {}) as Record<string, string | number | boolean>,
    });
  };
  return (
    <Card
      title={t('opsSettings.alarms.title')}
      actions={
        <select
          className="input"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          aria-label={t('opsSettings.alarms.scope')}
        >
          <option value="">{t('opsSettings.alarms.scopeGlobal')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      }
      bodyClass=""
    >
      <div className="card-body" style={{ paddingBottom: 0 }}>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('opsSettings.alarms.intro')}
        </p>
        {!canEdit && <p className="notice">{t('opsSettings.alarms.readOnly')}</p>}
        {error && (
          <p className="form-error">
            {t(`errors.${error}` as 'errors.INTERNAL_ERROR', { defaultValue: error })}
          </p>
        )}
      </div>
      <div className="ops-scroll">
        <table className="table policy-table">
          <thead>
            <tr>
              <th>{t('opsSettings.alarms.type')}</th>
              <th>{t('opsSettings.alarms.enabled')}</th>
              <th>{t('opsSettings.alarms.severity')}</th>
              <th>{t('opsSettings.alarms.scopeCol')}</th>
              <th>{t('opsSettings.alarms.description')}</th>
            </tr>
          </thead>
          <tbody>
            {(types.data?.items ?? []).map((x) => {
              const cur = own(x);
              const name = t(`alarmTypes.${x.key}.name` as 'alarmTypes.device_offline.name', {
                defaultValue: x.name_en,
              });
              const isOffline = x.key === 'device_offline';
              const mode = (x.effective.params.mode as string | undefined) ?? 'always';
              return (
                <tr key={x.key}>
                  <td>
                    <strong>{name}</strong>
                    <span className="cell-sub">
                      {t(`alarmCategory.${x.category}` as 'alarmCategory.device', {
                        defaultValue: x.category,
                      })}
                    </span>
                  </td>
                  <td>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={x.effective.enabled}
                        disabled={!canEdit || save.isPending}
                        onChange={(e) => put(x, { enabled: e.target.checked })}
                      />
                      {x.effective.enabled ? t('opsSettings.alarms.on') : t('opsSettings.alarms.masked')}
                    </label>
                    {isOffline && x.effective.enabled && (
                      <div style={{ marginTop: 6 }}>
                        <select
                          className="input"
                          value={mode}
                          disabled={!canEdit}
                          onChange={(e) =>
                            put(x, { params: { ...(cur?.params ?? {}), mode: e.target.value } })
                          }
                          aria-label={t('opsSettings.alarms.offlineMode')}
                        >
                          <option value="always">{t('opsSettings.alarms.modeAlways')}</option>
                          <option value="operating_hours">{t('opsSettings.alarms.modeOperating')}</option>
                        </select>
                      </div>
                    )}
                  </td>
                  <td>
                    <select
                      className="input"
                      value={cur?.severity ?? ''}
                      disabled={!canEdit}
                      onChange={(e) => put(x, { severity: (e.target.value || null) as Severity | null })}
                      aria-label={t('opsSettings.alarms.severity')}
                    >
                      <option value="">
                        {t('opsSettings.alarms.inherit', {
                          value: t(`severity.${scope ? x.effective.severity : x.default_severity}`),
                        })}
                      </option>
                      {(['info', 'warning', 'critical'] as const).map((s) => (
                        <option key={s} value={s}>
                          {t(`severity.${s}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className="pill">
                      {x.effective.source === 'default'
                        ? t('opsSettings.alarms.sourceDefault')
                        : x.effective.source === 'global'
                          ? t('opsSettings.alarms.sourceGlobal')
                          : t('opsSettings.alarms.sourceSite', {
                              site:
                                sites.find((s) => s.id === x.effective.source)?.name ??
                                x.overrides.find((o) => o.site_id === x.effective.source)?.site_name ??
                                '…',
                            })}
                    </span>
                    {!scope && x.overrides.length > 0 && (
                      <div className="override-list">
                        {x.overrides.map((o) => (
                          <span key={o.id} className="cell-sub">
                            {o.site_name}:{' '}
                            {o.enabled === null
                              ? ''
                              : o.enabled
                                ? t('opsSettings.alarms.on')
                                : t('opsSettings.alarms.masked')}
                            {o.severity ? ` · ${t(`severity.${o.severity}`)}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                    {cur && canEdit && (
                      <div>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ marginTop: 4 }}
                          onClick={() => reset.mutate(cur.id)}
                        >
                          {scope ? t('opsSettings.alarms.resetSite') : t('opsSettings.alarms.resetGlobal')}
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="policy-desc">
                      {t(`alarmTypes.${x.key}.description` as 'alarmTypes.device_offline.description', {
                        defaultValue: x.description_en,
                      })}
                    </div>
                    <div className="chips" style={{ marginTop: 6 }}>
                      {x.detectors.length === 0 && (
                        <span className="pill">{t('opsSettings.alarms.noDetector')}</span>
                      )}
                      {[...new Set(x.detectors.map((d) => d.protocol ?? '*'))].map((pr) =>
                        pr === '*' ? (
                          <span key={pr} className="pill">
                            {t('opsSettings.alarms.generic')}
                          </span>
                        ) : (
                          <ProtocolTag key={pr} protocol={pr} />
                        ),
                      )}
                    </div>
                    <span className="cell-sub" title={x.detectors.map((d) => d.name_en).join('\n')}>
                      {t('opsSettings.alarms.counts', {
                        faults: x.active_faults,
                        alarms: x.active_alarms,
                        detectors: x.detectors.length,
                      })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
